import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import { ConfigProvider, Input, List, Spin } from 'antd'
import CantileverButton from 'components/CantileverButton'
import dayjs from 'dayjs'
import { CommentItem } from 'features/assetDatabase/components/assetOverview/assetDetails/CommentItem'
import { useRolodexEdit } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex/'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { GeckoUnit, RolodexEntryPutJson, RolodexEntryType } from 'types'
import { v4 as uuid4 } from 'uuid'

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 12rem;
  background-color: ${({ theme }) => theme.forecast.background3};
  overflow-y: auto;
  border-radius: 8px;
`

const CommentsSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  width: 50%;
`

const AddCommentContainer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 8px;
  margin-top: 0.5rem;
  height: auto;
`

const EmptyCommentsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface CommentSectionProps {
  unit: GeckoUnit
  style?: React.CSSProperties
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  unit,
  style,
}) => {
  const { theme } = useGeckoTheme()
  const { rolodexPut } = useRolodexEdit()
  const { user: currentUser } = useGeckoPermissions()
  const [isLoading, setIsLoading] = useState(false)
  const [newComment, setNewComment] = useState<string | undefined>(undefined)

  const {
    userComments,
    isLoading: isLoadingComments,
    forceRefresh: refreshComments,
  } = useRolodexEntries({
    type: RolodexEntryType.USER_COMMENT,
    tags: {
      [RolodexEntryType.SITE]: unit.tags.site ?? '',
      [RolodexEntryType.ORGANIZATION]: unit.tags.organization ?? '',
      [RolodexEntryType.UNIT]: unit.id ?? '',
    },
    order_by: [{ key: 'timestamp', direction: 'desc' }],
  })

  // List can't take a readonly array, so we need to convert it to a mutable array
  const comments = useMemo(() => {
    return [...userComments]
  }, [userComments])

  const handleAddComment = async (comment: string | undefined) => {
    if (!currentUser) {
      console.error('No current user')
      return
    }

    if (!comment || comment.length === 0 || !unit) {
      console.error('No comment or unit')
      return
    }

    setIsLoading(true)

    const commentId = uuid4()
    const currentTimestamp = dayjs().unix()

    const patchData: RolodexEntryPutJson = {
      id: commentId,
      type: RolodexEntryType.USER_COMMENT,
      data: {
        user_id: currentUser.id,
        user_name: currentUser.name,
        text: comment,
        timestamp: currentTimestamp,
      },
      tags: {
        [RolodexEntryType.SITE]: unit?.tags.site ?? '',
        [RolodexEntryType.ORGANIZATION]: unit?.tags.organization ?? '',
        [RolodexEntryType.UNIT]: unit?.id ?? '',
      },
    }

    try {
      await rolodexPut([patchData]).then(() => {
        refreshComments()
        setNewComment(undefined)
      })
    } catch (error) {
      console.error(error)
    }

    setIsLoading(false)
  }

  return (
    <CommentsSectionContainer style={style}>
      <Spin spinning={isLoadingComments}>
        <CommentsContainer>
          <ConfigProvider
            renderEmpty={() => (
              <EmptyCommentsContainer>No comments</EmptyCommentsContainer>
            )}
            theme={{
              components: {
                List: { colorSplit: theme.forecast.background3 },
              },
            }}>
            <List
              itemLayout='horizontal'
              dataSource={comments}
              renderItem={(item) => (
                <CommentItem comment={item} forceRefresh={refreshComments} />
              )}
            />
          </ConfigProvider>
        </CommentsContainer>
      </Spin>
      <Spin spinning={isLoading}>
        <AddCommentContainer>
          <Input
            variant='borderless'
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder='Add a comment...'
            disabled={isLoading}
          />
          <CantileverButton
            style={{ width: '45%', minWidth: '8rem', margin: '0.5rem' }}
            onClick={() => {
              handleAddComment(newComment)
            }}
            disabled={isLoading}>
            <PlusCircleOutlined />
            Add Comment
          </CantileverButton>
        </AddCommentContainer>
      </Spin>
    </CommentsSectionContainer>
  )
}
